/**
 * SessionWarning.jsx — A1: Identity & Access Hardening
 * 
 * PURPOSE:
 * Alert users when their session is about to expire.
 * Allow them to extend or logout.
 * 
 * PLACEMENT:
 * Fixed position, always visible when warning is active.
 */

import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { AlertTriangle, Clock, RefreshCw, LogOut } from 'lucide-react';

export const SessionWarning = () => {
  const { sessionWarning, extendSession, logout } = useAuth();
  
  if (!sessionWarning) return null;
  
  return (
    <div 
      className="fixed bottom-4 right-4 z-50 max-w-sm animate-in slide-in-from-bottom-4"
      data-testid="session-warning"
    >
      <div 
        className="p-4 rounded-lg"
        style={{ 
          background: 'var(--g-warning-bg)', 
          border: '1px solid var(--g-warning-border)',
          boxShadow: 'var(--g-shadow-mid)'
        }}
      >
        <div className="flex items-start gap-3">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--g-warning)' }}
          >
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium" style={{ color: 'var(--g-warning)' }}>
              Session Expiring Soon
            </h4>
            <p className="text-sm mt-1" style={{ color: 'var(--g-warning)' }}>
              Your session will expire due to inactivity. Click below to stay signed in.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button 
                size="sm"
                onClick={extendSession}
                className="text-xs"
                style={{ 
                  background: 'var(--g-warning)', 
                  color: 'white',
                  border: 'none'
                }}
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Stay Signed In
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-xs"
                style={{ color: 'var(--g-warning)' }}
              >
                <LogOut className="w-3 h-3 mr-1" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionWarning;
